import React, { useEffect, useState } from "react";
import CustomForm from "../../../custom/Forms/CustomForm";
import { useParams } from "react-router-dom";
import { Button, Col, Row } from "reactstrap";
import MultiSelectInput from "../../../custom/components/MultiSelect-Input/MultiSelectInput";
import DownloadExcel, { getRecords } from "../../../services/api.service";
import SelectInput from "../../../custom/components/Select-Input/SelectInput";

const UserWiseReport = () => {
  const { id } = useParams;
  const [loadingData, setLoadingData] = useState(id ? true : false);
  const [loadingOptions, setLoadingOptions] = useState({
    assignedTo: false,
    items: false,
  });

  const [assignedToSelect, setAssignedToSelect] = useState(null);
  const [assignedToOptions, setAssignedToOptions] = useState([]);
  const [itemSelect, setItemSelect] = useState(null);
  const [itemSelectOptions, setItemSelectOptions] = useState([]);

  const [formErrors, setFormErrors] = useState({
    assignedToSelect: null,
    itemSelect: null,
  });

  const [searchKeyword, setSearchKeyword] = useState("");
  const [reportData, setReportData] = useState([]);

  const project_id = JSON.parse(localStorage.getItem("projectID"));

  useEffect(() => {
    const fetchAssignedToOptions = async () => {
      if (!project_id) return;

      setLoadingOptions((prev) => ({ ...prev, assignedTo: true }));
      try {
        const payload = { project_id };
        const response = await getRecords(payload, "/getUsersByProject");

        if (response && response.data) {
          const options = response.data.map((user) => ({
            value: user.id,
            label: user.first_name,
          }));
          setAssignedToOptions(options);
        }
      } catch (error) {
        console.error("Error fetching assigned to options:", error);
      } finally {
        setLoadingOptions((prev) => ({ ...prev, assignedTo: false }));
      }
    };

    fetchAssignedToOptions();
  }, [project_id]);

  useEffect(() => {
    const fetchItemOptions = async () => {
      if (!project_id) return;

      setLoadingOptions((prev) => ({ ...prev, items: true }));
      try {
        const payload = { project_id };
        const response = await getRecords(payload, "/inwardBuyItemList");

        if (response && response.data) {
          const options = response.data.map((item) => ({
            value: item.id,
            label: item.item_code,
          }));

          setItemSelectOptions([
            { value: "all", label: "Select All" },
            ...options,
          ]);
        }
      } catch (error) {
        console.error("Error fetching item options:", error);
      } finally {
        setLoadingOptions((prev) => ({ ...prev, items: false }));
      }
    };

    fetchItemOptions();
  }, [project_id]);

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!assignedToSelect) {
      errors.assignedToSelect = "Assigned To is required";
      isValid = false;
    }
    if (!itemSelect || itemSelect.length === 0) {
      errors.itemSelect = "Item is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleAssignedToSelect = (selectedOption) => {
    setAssignedToSelect(selectedOption);
    setFormErrors((prev) => ({ ...prev, assignedToSelect: null }));
    if (!selectedOption) {
      setItemSelect([]);
      setReportData([]);
    }
  };

  const handleItemSelect = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setItemSelect([]);
      setFormErrors((prev) => ({ ...prev, itemSelect: null }));
      setReportData([]);
      return;
    }

    const allOption = selectedOptions.find((opt) => opt.value === "all");
    if (allOption) {
      setItemSelect(itemSelectOptions.slice(1));
    } else {
      setItemSelect(selectedOptions);
    }
    setFormErrors((prev) => ({ ...prev, itemSelect: null }));
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchKeyword(value);
  };

  const handleGo = async () => {
    if (!validateForm()) return;

    const user_id = assignedToSelect?.value;
    const buy_item_id = itemSelect.map((item) => item.value);

    const payload = {
      project_id,
      user_id,
      buy_item_id,
    };
    try {
      setLoadingData(true);
      const response = await getRecords(payload, "/userwiseReport");
      if (Array.isArray(response)) {
        setReportData(response);
      } else if (response?.data && Array.isArray(response.data)) {
        setReportData(response.data);
      } else {
        setReportData([]);
      }
    } catch (error) {
      console.error("Error fetching user-wise report:", error);
      setReportData([]);
    } finally {
      setLoadingData(false);
    }
  };

  const handleExportData = () => {
    if (filteredData.length === 0) {
      alert("No data to export");
      return;
    }

    const columns = [
      { label: "Sr No", key: "srNo" },
      { label: "Item Code", key: "itemCode" },
      { label: "Item Description", key: "itemDesc" },
      { label: "Assigned To", key: "assignedTo" },
      { label: "Outward Qty", key: "outwardQty" },
      { label: "Consumed Qty", key: "consumedQty" },
      { label: "Onhand Qty", key: "onhandQty" },
    ];

    const rows = filteredData.map((item, index) => ({
      srNo: item["Sr No"] || index + 1,
      itemCode: item["Item Code"] || "",
      itemDesc: item["Item Desc"] || "",
      assignedTo: item["Assigned to"] || "",
      outwardQty: item["Outward Qty"] || 0,
      consumedQty: item["Consumed Qty"] || 0,
      onhandQty: item["Onhand Qty"] || 0,
    }));

    DownloadExcel({
      fileName: "UserWiseReport",
      columns: columns,
      data: rows,
      cellColor: false,
    });
  };

  const filteredData = searchKeyword.trim()
    ? reportData.filter((item) =>
        item["Item Code"]?.toLowerCase().includes(searchKeyword.toLowerCase())
      )
    : reportData;

  return (
    <CustomForm
      title={"USer Wise Report"}
      loadingData={loadingData}
      noBackButton={true}
      noSubmitButton={true}
    >
      <Row>
        <Col md="4" className="mt-3">
          <SelectInput
            label="Assigned To"
            value={assignedToSelect}
            options={assignedToOptions}
            onSelect={handleAssignedToSelect}
            placeholder="Select assigned to"
            labelClassName="form-label"
            isClearable={true}
            asterisk={true}
            isLoading={loadingOptions.assignedTo}
            customError={formErrors.assignedToSelect}
          />
        </Col>
        <Col md="4" className="mt-3">
          <MultiSelectInput
            label="Item"
            value={itemSelect}
            options={itemSelectOptions}
            onMultiSelect={(selected) => handleItemSelect(selected)}
            placeholder="Select Item"
            labelClassName="form-label"
            isClearable={true}
            asterisk={true}
            customError={formErrors.itemSelect}
          />
        </Col>
        <Col md="4" className="d-flex align-items-end mt-3">
          <Button
            onClick={handleGo}
            color="primary"
            type="button"
            className="waves-effect waves-light me-1"
            style={{ background: "#006eb9" }}
          >
            Go
          </Button>
        </Col>
      </Row>

      <Row className="justify-content-center mt-4">
        <Col
          style={{
            marginBottom: "0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div className="app-search d-flex d-lg-inline mt-0">
            <div className="position-relative">
              <input
                type="search"
                className="form-control ps-5"
                placeholder="Search by item code.."
                value={searchKeyword}
                onChange={handleInputChange}
                style={{
                  width: "13rem",
                  padding: "8px 15px",
                  borderRadius: "8px",
                  borderColor: "#D2D2D2",
                  outline: "none",
                  transition: "all 0.3s ease",
                  boxShadow: "none",
                  marginRight: "0.5rem",
                }}
              />
              <span className="ri-search-line"></span>
            </div>
          </div>
          <Button
            onClick={handleExportData}
            color="success"
            disabled={filteredData.length === 0}
          >
            Download CSV
          </Button>
        </Col>
        <div
          className="table mt-2"
          style={{
            maxHeight: "450px",
            overflowY: "auto",
            width: "100%",
            scrollBehavior: "smooth",
          }}
        >
          <table className="table table-hover">
            <thead
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "#8AC1E8",
                zIndex: 1,
              }}
            >
              <tr style={{ borderBottom: "1px solid black" }}>
                <th style={{ whiteSpace: "nowrap" }}>Sr. No</th>
                <th style={{ whiteSpace: "nowrap" }}>Item Code</th>
                <th style={{ whiteSpace: "nowrap" }}>Item Desc</th>
                <th style={{ whiteSpace: "nowrap" }}>Assigned To</th>
                <th style={{ whiteSpace: "nowrap" }}>Outward Qty</th>
                <th style={{ whiteSpace: "nowrap" }}>Consumed Qty</th>
                <th style={{ whiteSpace: "nowrap" }}>Onhand Qty</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    No records found.
                  </td>
                </tr>
              ) : (
                filteredData.map((item, index) => (
                  <tr key={index}>
                    <td>{item["Sr No"]}</td>
                    <td>{item["Item Code"]}</td>
                    <td>{item["Item Desc"]}</td>
                    <td>{item["Assigned to"]}</td>
                    <td>{item["Outward Qty"]}</td>
                    <td>{item["Consumed Qty"]}</td>
                    <td>{item["Onhand Qty"]}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Row>
    </CustomForm>
  );
};

export default UserWiseReport;
